/**
 * Bulk import of services from a spreadsheet.
 *
 * The division of labour matters: the AGENT decides the column mapping from a
 * small sample, and this module applies that mapping to every row in plain
 * code. A thousand rows through the model would be slow, expensive, and would
 * not treat row 900 the same way as row 3.
 *
 * Everything here is pure and synchronous over an already-parsed sheet, so it
 * is testable without a browser and cannot touch anything but the local store.
 */

import { read, utils } from "xlsx"
import { ulid } from "ulid"
import {
  type ServiceStore, type EntityKind, type ServiceStatus,
  SERVICE_STATUSES, displayName, BUCKET,
} from "./types"
import * as db from "./store"

// ── Parsing ──────────────────────────────────────────────────────────────────

export interface ParsedSheet {
  headers: string[]
  rows: Array<Record<string, string>>
  /** Sheet names found, when the upload was a workbook rather than a CSV. */
  sheetNames: string[]
  activeSheet: string
}

/**
 * Reads a CSV or spreadsheet into rows of strings.
 *
 * Values are kept as text: postal codes, phone numbers and ids all lose
 * meaning when coerced to numbers ("07123" becoming 7123, say).
 */
export function parseSheet(data: ArrayBuffer, sheetName?: string): ParsedSheet {
  const wb = read(data, { type: "array", raw: false })
  const sheetNames = wb.SheetNames
  const active = sheetName && sheetNames.includes(sheetName) ? sheetName : sheetNames[0]
  if (!active) return { headers: [], rows: [], sheetNames, activeSheet: "" }

  const raw = utils.sheet_to_json<Record<string, unknown>>(wb.Sheets[active], {
    defval: "",
    raw: false,
    blankrows: false,
  })

  const rows = raw.map(r => {
    const out: Record<string, string> = {}
    for (const [k, v] of Object.entries(r)) out[String(k).trim()] = v == null ? "" : String(v).trim()
    return out
  })

  /**
   * Header names must be the keys the ROWS are actually under, not the raw
   * header row.
   *
   * xlsx deduplicates repeated headers when it builds row objects: a sheet
   * with two "Phone" columns yields keys "Phone" and "Phone_1". Reading names
   * from the header row instead would show "Phone" twice, and since a mapping
   * is a plain object keyed by column name, only one could ever be mapped —
   * the second column's data would be silently unreachable. Duplicate headers
   * are common in real exports (repeated Phone/Category columns), which is
   * exactly what the multi-column-per-field path exists to support.
   *
   * Keys are collected in document order across all rows, because a column
   * that is blank in the first row still appears in later ones.
   */
  const seen = new Set<string>()
  for (const r of rows) for (const k of Object.keys(r)) seen.add(k)
  // __EMPTY / __EMPTY_1 are xlsx's synthetic names for unheaded columns.
  const headers = [...seen].filter(h => h && !h.startsWith("__EMPTY"))

  return { headers, rows, sheetNames, activeSheet: active }
}

// ── Mapping ──────────────────────────────────────────────────────────────────

/** Fields a column can be mapped onto. */
export type MappedField =
  | "name" | "description" | "address"
  | "providerName" | "country" | "region" | "city"
  | "categories" | "tags" | "populations" | "accessibility"
  | "lat" | "lon" | "status"
  | "phone" | "email" | "website" | "whatsapp"
  | "externalId"
  | "ignore"

export interface ColumnMapping {
  /** Sheet column header → the field it fills. */
  columns: Record<string, MappedField>
  /** Separator for multi-value cells such as categories. */
  multiValueSeparator?: string
  /** Locale the text columns are written in. */
  locale?: string
  /** Column holding a stable id, used to match rows to existing services. */
  idColumn?: string
}

const CONTACT_FIELDS: Partial<Record<MappedField, string>> = {
  phone: "phone",
  email: "email",
  website: "website",
  whatsapp: "whatsapp",
}

/**
 * A first guess at the mapping from header names alone.
 *
 * Only a starting point — the agent refines it against sample rows, because
 * headers lie ("Location" might be a city, an address, or coordinates).
 */
export function guessMapping(headers: string[]): ColumnMapping {
  const columns: Record<string, MappedField> = {}
  const norm = (h: string) => h.toLowerCase().replace(/[^a-z0-9]/g, "")

  const rules: Array<[RegExp, MappedField]> = [
    [/^(service|program|servicename|programname|title)$/, "name"],
    [/^(description|details|summary|about|servicedescription)$/, "description"],
    [/^(address|streetaddress|location|physicaladdress)$/, "address"],
    [/^(provider|organization|organisation|org|agency|providername)$/, "providerName"],
    [/^(country)$/, "country"],
    [/^(region|state|province|department)$/, "region"],
    [/^(city|town|municipality|locality)$/, "city"],
    [/^(category|categories|servicecategory|servicecategories)$/, "categories"],
    [/^(tag|tags|keywords|subcategory|subcategories)$/, "tags"],
    [/^(population|populations|populationsserved|audience)$/, "populations"],
    [/^(accessibility|accessibilityoptions|access)$/, "accessibility"],
    [/^(lat|latitude)$/, "lat"],
    [/^(lon|lng|long|longitude)$/, "lon"],
    [/^(status|publishstatus|state)$/, "status"],
    [/^(phone|telephone|phonenumber|tel)$/, "phone"],
    [/^(email|emailaddress)$/, "email"],
    [/^(website|url|weblink|site)$/, "website"],
    [/^(whatsapp|whatsappnumber)$/, "whatsapp"],
    [/^(id|externalid|serviceid|recordid|uid)$/, "externalId"],
  ]

  for (const h of headers) {
    const n = norm(h)
    const hit = rules.find(([re]) => re.test(n))
    columns[h] = hit ? hit[1] : "ignore"
  }
  return { columns, multiValueSeparator: ";", idColumn: headers.find(h => columns[h] === "externalId") }
}

// ── Planning ─────────────────────────────────────────────────────────────────

export interface NewValue {
  kind: EntityKind
  name: string
  /** Pass this to applyImport's approvedNewValues to allow creating it. */
  key: string
  /** How many rows reference it, so bulk typos stand out from real values. */
  rowCount: number
  /** An existing name this closely resembles, if any. */
  similarTo?: string
}

export interface RowIssue {
  /** 1-based row number as it appears in the sheet, header included. */
  row: number
  field: string
  message: string
  value?: string
}

export interface ImportPlan {
  totalRows: number
  toCreate: number
  toUpdate: number
  /** Rows that cannot be imported at all, e.g. with no name. */
  toSkip: number
  /** Records that would be created because nothing matched. Needs approval. */
  newValues: NewValue[]
  /** Rows importable but imperfect, for the agent to resolve afterwards. */
  issues: RowIssue[]
  mapping: ColumnMapping
}

const SEP_FALLBACK = /[;|]/

function splitValues(value: string, sep?: string): string[] {
  if (!value) return []
  const parts = sep ? value.split(sep) : value.split(SEP_FALLBACK)
  return parts.map(v => v.trim()).filter(Boolean)
}

/**
 * Stable key identifying a value awaiting approval.
 *
 * Callers must use this rather than composing the string themselves — the
 * normalisation has to match what applyImport checks, or approvals silently
 * fail to apply.
 */
export function approvalKey(kind: EntityKind, name: string): string {
  return `${kind}:${matchKey(name)}`
}

/** Case- and accent-insensitive key for matching sheet text to stored names. */
function matchKey(s: string): string {
  return s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/\s+/g, " ").trim()
}

function findByName(store: ServiceStore, kind: EntityKind, name: string, locale: string): any | null {
  const target = matchKey(name)
  if (!target) return null
  for (const e of db.listEntities(store, kind)) {
    if (matchKey(displayName(e, locale, store.defaultLocale)) === target) return e
    if (e.name && matchKey(e.name) === target) return e
    // A record may only carry the name in some other locale.
    for (const t of Object.values(e.translations ?? {}) as any[]) {
      if (t?.name && matchKey(t.name) === target) return e
    }
  }
  return null
}

/** Rough similarity, to flag "Legal aid" against an existing "Legal Aid". */
function similarity(a: string, b: string): number {
  const x = matchKey(a)
  const y = matchKey(b)
  if (!x || !y) return 0
  if (x === y) return 1
  if (x.includes(y) || y.includes(x)) return 0.9
  const setA = new Set(x.split(""))
  const setB = new Set(y.split(""))
  let shared = 0
  for (const c of setA) if (setB.has(c)) shared++
  return shared / Math.max(setA.size, setB.size)
}

function valueFor(row: Record<string, string>, mapping: ColumnMapping, field: MappedField): string {
  for (const [header, mapped] of Object.entries(mapping.columns)) {
    if (mapped === field) {
      const v = row[header]
      if (v) return v
    }
  }
  return ""
}

/**
 * Works out what an import would do, without changing anything.
 *
 * Returned so the user can approve new taxonomy and geography before any of it
 * is written — the point being that a typo in one cell should not silently add
 * a category that then looks legitimate.
 */
export function planImport(
  store: ServiceStore,
  sheet: ParsedSheet,
  mapping: ColumnMapping,
  /** Plan only the first N rows, so a trial run's preview matches what it will actually write. */
  opts: { limit?: number } = {},
): ImportPlan {
  const locale = mapping.locale || store.defaultLocale
  const issues: RowIssue[] = []
  const newCounts = new Map<string, { kind: EntityKind; name: string; rows: number }>()

  let toCreate = 0
  let toUpdate = 0
  let toSkip = 0

  const noteNew = (kind: EntityKind, name: string) => {
    const key = approvalKey(kind, name)
    const existing = newCounts.get(key)
    if (existing) existing.rows++
    else newCounts.set(key, { kind, name, rows: 1 })
  }

  const planned = opts.limit ? sheet.rows.slice(0, opts.limit) : sheet.rows
  planned.forEach((row, i) => {
    const rowNo = i + 2 // header is row 1
    const name = valueFor(row, mapping, "name")
    if (!name) {
      toSkip++
      issues.push({ row: rowNo, field: "name", message: "No service name; row cannot be imported." })
      return
    }

    const providerName = valueFor(row, mapping, "providerName")
    const existing = findExistingService(store, {
      name,
      providerName,
      externalId: mapping.idColumn ? row[mapping.idColumn] : "",
      locale,
    })
    if (existing) toUpdate++
    else toCreate++

    if (providerName && !findByName(store, "provider", providerName, locale)) noteNew("provider", providerName)

    for (const [field, kind] of [
      ["country", "country"], ["region", "region"], ["city", "city"],
    ] as Array<[MappedField, EntityKind]>) {
      const v = valueFor(row, mapping, field)
      if (v && !findByName(store, kind, v, locale)) noteNew(kind, v)
    }

    for (const [field, kind] of [
      ["categories", "serviceCategory"], ["tags", "tag"],
      ["populations", "population"], ["accessibility", "accessibility"],
    ] as Array<[MappedField, EntityKind]>) {
      for (const v of splitValues(valueFor(row, mapping, field), mapping.multiValueSeparator)) {
        if (!findByName(store, kind, v, locale)) noteNew(kind, v)
      }
    }

    const status = valueFor(row, mapping, "status")
    if (status && !SERVICE_STATUSES.includes(status as ServiceStatus)) {
      issues.push({ row: rowNo, field: "status", value: status, message: `Unknown status; will import as draft.` })
    }

    for (const f of ["lat", "lon"] as const) {
      const v = valueFor(row, mapping, f)
      if (v && Number.isNaN(Number(v))) {
        issues.push({ row: rowNo, field: f, value: v, message: "Not a number; coordinates will be left empty." })
      }
    }
  })

  // Flag likely typos: a new value that closely resembles something existing.
  const newValues: NewValue[] = [...newCounts.values()].map(({ kind, name, rows }) => {
    let similarTo: string | undefined
    let best = 0
    for (const e of db.listEntities(store, kind)) {
      const other = displayName(e, locale, store.defaultLocale)
      const score = similarity(name, other)
      if (score > best && score >= 0.8 && matchKey(other) !== matchKey(name)) {
        best = score
        similarTo = other
      }
    }
    return { kind, name, key: approvalKey(kind, name), rowCount: rows, similarTo }
  }).sort((a, b) => b.rowCount - a.rowCount)

  // totalRows reports what was PLANNED, so the review card's counts and its
  // "N of M rows" header describe the same set of rows.
  return { totalRows: planned.length, toCreate, toUpdate, toSkip, newValues, issues, mapping }
}

// ── Matching ─────────────────────────────────────────────────────────────────

/**
 * Finds the service a row refers to, so re-importing updates rather than
 * duplicates. An explicit id column wins; otherwise name plus provider, since
 * "IRC in Denver" is only unique within its organization.
 */
function findExistingService(
  store: ServiceStore,
  args: { name: string; providerName?: string; externalId?: string; locale: string },
): any | null {
  if (args.externalId) {
    for (const s of Object.values(store.services) as any[]) {
      if (s.externalId && String(s.externalId) === String(args.externalId)) return s
    }
  }

  const nameKey = matchKey(args.name)
  const provider = args.providerName
    ? findByName(store, "provider", args.providerName, args.locale)
    : null

  for (const s of Object.values(store.services) as any[]) {
    if (matchKey(displayName(s, args.locale, store.defaultLocale)) !== nameKey) continue
    // Same name under a different provider is a different service.
    if (provider && s.providerId && s.providerId !== provider.id) continue
    return s
  }
  return null
}

// ── Applying ─────────────────────────────────────────────────────────────────

/**
 * Creates an entity directly inside `store`, bypassing db.createEntity's clone.
 *
 * Only safe because applyImport owns its draft; never call this on a store that
 * is live React state.
 */
function writeEntity(
  store: ServiceStore,
  kind: EntityKind,
  data: Record<string, any>,
  locale: string,
): string {
  const id = ulid()
  const ts = Date.now()
  const base: any = { id, createdAt: ts, updatedAt: ts, translations: {} }

  if (kind === "service") {
    Object.assign(base, {
      status: "draft",
      categoryIds: [], tagIds: [], populationIds: [], accessibilityIds: [],
      hours: [], contactInfo: [], consentForms: [],
    })
  }
  if (kind === "serviceCategory") Object.assign(base, { status: "draft" })
  if (kind === "country") Object.assign(base, { languageCodes: [] })
  if (kind === "provider") Object.assign(base, { status: "Active" })

  const entity = { ...base, ...data }
  if (data.name) {
    writeTranslation(entity, locale, {
      name: data.name,
      description: data.description,
      address: data.address,
      definition: data.definition,
    })
  }

  ;(store[BUCKET[kind]] as Record<string, any>)[id] = entity
  return id
}

/** Upserts one locale's translation on an entity, in place. */
function writeTranslation(entity: any, locale: string, patch: Record<string, any>): void {
  entity.translations ||= {}
  const existing = entity.translations[locale]
  entity.translations[locale] = {
    locale,
    name: patch.name ?? existing?.name ?? "",
    description: patch.description ?? existing?.description,
    address: patch.address ?? existing?.address,
    definition: patch.definition ?? existing?.definition,
    reviewed: patch.reviewed ?? existing?.reviewed,
    status: patch.status ?? existing?.status,
    updatedAt: Date.now(),
  }
  entity.updatedAt = Date.now()
}


export interface ImportResult {
  created: number
  updated: number
  skipped: number
  /** Records created to satisfy the rows, by kind. */
  createdSupporting: Record<string, number>
  issues: RowIssue[]
  store: ServiceStore
}

export interface ApplyOptions {
  /**
   * Values approved for creation, as "kind:name". Anything not listed is left
   * unset on the row rather than invented.
   */
  approvedNewValues?: Set<string>
  /** Import at most this many rows; for trialling a mapping. */
  limit?: number
}

/**
 * Applies a mapping to every row.
 *
 * Runs entirely in code so all rows are treated identically, and writes once
 * at the end so React re-renders a single time rather than per row.
 */
export function applyImport(
  store: ServiceStore,
  sheet: ParsedSheet,
  mapping: ColumnMapping,
  opts: ApplyOptions = {},
): ImportResult {
  const locale = mapping.locale || store.defaultLocale
  const approved = opts.approvedNewValues
  const issues: RowIssue[] = []
  const createdSupporting: Record<string, number> = {}

  // One clone for the whole run: the store helpers copy the entire store per
  // call, which turns a thousand rows into a thousand full copies. This is our
  // private draft until it is returned, so mutating it in place is safe.
  const next: ServiceStore = JSON.parse(JSON.stringify(store))

  // Name lookups are the other quadratic cost — findByName scans every record
  // of a kind, once per row per field. Index them once and keep it current.
  const index = new Map<string, string>()
  const indexKey = (kind: EntityKind, name: string) => `${kind}:${matchKey(name)}`
  for (const kind of Object.keys(BUCKET) as EntityKind[]) {
    for (const e of db.listEntities(next, kind)) {
      const names = new Set<string>()
      if (e.name) names.add(e.name)
      for (const t of Object.values(e.translations ?? {}) as any[]) if (t?.name) names.add(t.name)
      for (const n of names) {
        const k = indexKey(kind, n)
        if (!index.has(k)) index.set(k, e.id)
      }
    }
  }

  let created = 0
  let updated = 0
  let skipped = 0

  const mayCreate = (kind: EntityKind, name: string) =>
    !approved || approved.has(approvalKey(kind, name))

  /** Resolves a name to an id, creating the record when that was approved. */
  const resolve = (
    kind: EntityKind,
    name: string,
    extra: Record<string, any>,
    rowNo: number,
    field: string,
  ): string | undefined => {
    if (!name) return undefined
    const hit = index.get(indexKey(kind, name))
    if (hit) return hit
    if (!mayCreate(kind, name)) {
      issues.push({ row: rowNo, field, value: name, message: `No matching ${kind} and creation was not approved; left empty.` })
      return undefined
    }
    const id = writeEntity(next, kind, { name, ...extra }, locale)
    index.set(indexKey(kind, name), id)
    createdSupporting[kind] = (createdSupporting[kind] ?? 0) + 1
    return id
  }

  const rows = opts.limit ? sheet.rows.slice(0, opts.limit) : sheet.rows

  rows.forEach((row, i) => {
    const rowNo = i + 2
    const name = valueFor(row, mapping, "name")
    if (!name) { skipped++; return }

    // Geography must resolve top-down: a city needs its region and country.
    const countryName = valueFor(row, mapping, "country")
    const countryId = resolve("country", countryName, {}, rowNo, "country")

    const regionName = valueFor(row, mapping, "region")
    const regionId = regionName && countryId
      ? resolve("region", regionName, { countryId }, rowNo, "region")
      : undefined
    if (regionName && !countryId) {
      issues.push({ row: rowNo, field: "region", value: regionName, message: "Region needs a country; left empty." })
    }

    const cityName = valueFor(row, mapping, "city")
    const cityId = cityName && countryId && regionId
      ? resolve("city", cityName, { countryId, regionId }, rowNo, "city")
      : undefined
    if (cityName && (!countryId || !regionId)) {
      issues.push({ row: rowNo, field: "city", value: cityName, message: "City needs both a country and a region; left empty." })
    }

    const providerName = valueFor(row, mapping, "providerName")
    const providerId = providerName
      ? resolve("provider", providerName, { countryId, status: "Active" }, rowNo, "provider")
      : undefined

    const multi = (field: MappedField, kind: EntityKind): string[] => {
      const out: string[] = []
      for (const v of splitValues(valueFor(row, mapping, field), mapping.multiValueSeparator)) {
        const id = resolve(kind, v, {}, rowNo, field)
        if (id) out.push(id)
      }
      return out
    }

    const contactInfo: Array<{ channel: string; contactDetails: string }> = []
    for (const [field, channel] of Object.entries(CONTACT_FIELDS) as Array<[MappedField, string]>) {
      const v = valueFor(row, mapping, field)
      if (v) contactInfo.push({ channel, contactDetails: v })
    }

    const num = (field: "lat" | "lon"): number | undefined => {
      const v = valueFor(row, mapping, field)
      if (!v) return undefined
      const n = Number(v)
      return Number.isFinite(n) ? n : undefined
    }

    const rawStatus = valueFor(row, mapping, "status")
    const status: ServiceStatus = SERVICE_STATUSES.includes(rawStatus as ServiceStatus)
      ? (rawStatus as ServiceStatus)
      : "draft"

    /**
     * Whether the sheet actually has a column for this field.
     *
     * This matters for the UPDATE path. multi() returns [] and the status
     * fallback returns "draft" whether the column is missing or merely blank,
     * and neither is undefined — so both survive the undefined-sweep below and
     * would overwrite curated data on a partial re-import, wiping every
     * category/tag/population and demoting published services to draft.
     * Restricting them to columns the sheet supplies keeps a partial sheet
     * partial, which is the whole point of matching against existing rows.
     */
    const mapped = (f: MappedField) => Object.values(mapping.columns).includes(f)
    const multiIfMapped = (f: MappedField, kind: EntityKind) => mapped(f) ? multi(f, kind) : undefined

    const fields: Record<string, any> = {
      // Create supplies its own "draft" default below; only send a status here
      // when the sheet really carries one.
      status: mapped("status") ? status : undefined,
      countryId, regionId, cityId, providerId,
      categoryIds: multiIfMapped("categories", "serviceCategory"),
      tagIds: multiIfMapped("tags", "tag"),
      populationIds: multiIfMapped("populations", "population"),
      accessibilityIds: multiIfMapped("accessibility", "accessibility"),
      lat: num("lat"),
      lon: num("lon"),
      ...(contactInfo.length ? { contactInfo } : {}),
      ...(mapping.idColumn && row[mapping.idColumn] ? { externalId: row[mapping.idColumn] } : {}),
    }
    for (const k of Object.keys(fields)) if (fields[k] === undefined) delete fields[k]

    const description = valueFor(row, mapping, "description")
    const address = valueFor(row, mapping, "address")

    const existing = findExistingService(next, {
      name, providerName,
      externalId: mapping.idColumn ? row[mapping.idColumn] : "",
      locale,
    })

    if (existing) {
      const target = next.services[existing.id] as any
      Object.assign(target, fields, { updatedAt: Date.now() })
      writeTranslation(target, locale, {
        name,
        ...(description ? { description } : {}),
        ...(address ? { address } : {}),
      })
      updated++
    } else {
      // status default belongs on create, where there is nothing to preserve.
      const id = writeEntity(next, "service", { status, name, description, address, ...fields }, locale)
      index.set(indexKey("service", name), id)
      created++
    }
  })

  return { created, updated, skipped, createdSupporting, issues, store: next }
}
